import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button, Icon } from '@picsio/ui';
import { Info } from '@picsio/icons';

import localization from '../../../../shared/strings';
import confirmShopifyPrepareIfNeeded, {
  PREPARE_ACTIONS,
} from '../../../../helpers/shopify/confirmShopifyPrepareIfNeeded';
import {
  isShopifyChannelOption,
  pickShopifyMediaByChannel,
} from '../../../../helpers/shopify/listingMasterConsistency';
import { aggregateSyncMediaSelectionStateByChannel } from '../../../../helpers/shopify/resolveSyncMediaSelectionState';
import DialogIcon from '../../../Icon';
import Tooltip from '../../../Tooltip';
import ButtonLoader from '../ButtonLoader';
import ChannelTabsHeader from '../MediaSelection/ChannelTabsHeader';
import VariantMediaSection from '../MediaSelection/VariantMediaSection';
import getSyncMediaConfirmDisabledTooltip from '../helpers/getSyncMediaConfirmDisabledTooltip';
import {
  countAssignedVariantsForChannel,
  getAssignedAssetsFromVariantMedia,
} from '../helpers/syncFlowMediaHelpers';
import { CHANNEL_SYNC_ACTION } from '../helpers/syncFlowOrchestrator';
import { normalizeVariantId } from '../helpers/toggleVariantAssignment';
import {
  assignAssetToVariant,
  getDefaultExpandedVariantIds,
  groupAssetsByVariant,
  toggleExpandedVariantIds,
  unassignAssetFromVariant,
} from '../helpers/variantMediaAssignmentHelpers';
import styles from '../styles.module.scss';

function VariantMediaAssignmentDialog({
  channelOptions,
  selectedChannels,
  channelActionsByChannelId,
  variantOptions,
  variantMediaByChannel,
  validateBeforeFinish,
  onConfirm,
  onBack,
  onCancel,
  onValidationAbort,
  root,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;
  const mediaStrings = strings.VARIANT_MEDIA;

  const channels = useMemo(
    () => channelOptions.filter(({ value }) => selectedChannels.includes(value)),
    [channelOptions, selectedChannels],
  );

  const [activeChannelId, setActiveChannelId] = useState(channels[0]?.value);
  const [assignmentsByChannel, setAssignmentsByChannel] = useState(() => (
    selectedChannels.reduce((acc, channelId) => ({
      ...acc,
      [channelId]: { ...(variantMediaByChannel[channelId] || {}) },
    }), {})
  ));
  const [expandedVariantIds, setExpandedVariantIds] = useState(
    () => getDefaultExpandedVariantIds(variantOptions),
  );
  const [isConfirming, setIsConfirming] = useState(false);

  const activeChannel = channels.find(({ value }) => value === activeChannelId) || channels[0];
  const channelAssignments = assignmentsByChannel[activeChannel?.value] || {};
  const isMultiChannel = channels.length > 1;
  const totalVariantCount = variantOptions.length;

  const assignedCount = countAssignedVariantsForChannel({
    variantMediaByChannel: assignmentsByChannel,
    channelId: activeChannel?.value,
    totalVariantCount,
  });

  const assetsByVariant = useMemo(
    () => groupAssetsByVariant(
      channelAssignments,
      variantOptions.map(({ id }) => id),
    ),
    [channelAssignments, variantOptions],
  );

  const isDestroyedRef = useRef(false);

  const destroy = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    isDestroyedRef.current = true;

    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handlePrevious = useCallback(() => {
    destroy();
    onBack?.(assignmentsByChannel);
  }, [assignmentsByChannel, destroy, onBack]);

  const handleChannelChange = useCallback((channelId) => {
    setActiveChannelId(channelId);
    setExpandedVariantIds(getDefaultExpandedVariantIds(variantOptions));
  }, [variantOptions]);

  const updateChannelAssignments = useCallback((nextAssignments) => {
    setAssignmentsByChannel((prev) => ({
      ...prev,
      [activeChannel.value]: nextAssignments,
    }));
  }, [activeChannel.value]);

  const handleToggleExpand = useCallback((variantId) => {
    setExpandedVariantIds((prev) => toggleExpandedVariantIds(prev, variantId));
  }, []);

  const handleSelectAsset = useCallback((variantId, assetId) => {
    const normalizedVariantId = normalizeVariantId(variantId);
    const normalizedAssetId = String(assetId);
    const assignedAssetIds = assetsByVariant[normalizedVariantId] || [];
    const isAlreadyAssigned = assignedAssetIds.includes(normalizedAssetId);

    if (isAlreadyAssigned) {
      updateChannelAssignments(unassignAssetFromVariant({
        assignments: channelAssignments,
        assetId: normalizedAssetId,
        variantId: normalizedVariantId,
      }));
      return;
    }

    updateChannelAssignments(assignAssetToVariant({
      assignments: channelAssignments,
      assetId: normalizedAssetId,
      variantId: normalizedVariantId,
    }));
  }, [
    assetsByVariant,
    channelAssignments,
    updateChannelAssignments,
  ]);

  const handleResetAll = useCallback(() => {
    setAssignmentsByChannel((prev) => ({
      ...prev,
      [activeChannel.value]: {},
    }));
  }, [activeChannel.value]);

  const shopifyPrepareAssets = useMemo(
    () => getAssignedAssetsFromVariantMedia(
      variantOptions,
      pickShopifyMediaByChannel(assignmentsByChannel, channels),
    ),
    [variantOptions, assignmentsByChannel, channels],
  );
  const poolAssets = useMemo(() => {
    const byId = new Map();
    variantOptions.forEach(({ attachedAssets }) => {
      (attachedAssets || []).forEach((asset) => {
        if (asset?._id) {
          byId.set(String(asset._id), asset);
        }
      });
    });
    return [...byId.values()];
  }, [variantOptions]);
  const mediaByChannelForValidation = useMemo(
    () => Object.entries(assignmentsByChannel).reduce((acc, [channelId, assignments]) => {
      acc[channelId] = Object.keys(assignments || {});
      return acc;
    }, {}),
    [assignmentsByChannel],
  );
  const selectionAggregate = useMemo(
    () => aggregateSyncMediaSelectionStateByChannel({
      assets: poolAssets,
      mediaByChannel: mediaByChannelForValidation,
      channelActionsByChannelId,
      publishAction: CHANNEL_SYNC_ACTION.PUBLISH,
      channelOptions: channels,
    }),
    [poolAssets, mediaByChannelForValidation, channelActionsByChannelId, channels],
  );
  const isActiveShopifyChannel = isShopifyChannelOption(activeChannel);
  const requirePublishReadyForActiveChannel = (
    isActiveShopifyChannel
    && channelActionsByChannelId[activeChannel?.value] === CHANNEL_SYNC_ACTION.PUBLISH
  );
  const confirmDisabledTooltip = getSyncMediaConfirmDisabledTooltip(selectionAggregate);
  const isConfirmDisabled = selectionAggregate.hasHardSelected || isConfirming;

  const handleConfirm = useCallback(async () => {
    if (selectionAggregate.hasHardSelected) {
      return;
    }

    setIsConfirming(true);

    try {
      const prepareAction = await confirmShopifyPrepareIfNeeded(shopifyPrepareAssets);
      if (prepareAction === PREPARE_ACTIONS.ASSET_COPY) {
        // Leave sync wizard so the user can finish Asset Copy unobstructed.
        destroy();
        onCancel?.();
        return;
      }
      if (prepareAction !== PREPARE_ACTIONS.PROCEED) {
        setIsConfirming(false);
        return;
      }

      if (validateBeforeFinish) {
        try {
          await validateBeforeFinish(() => {
            setIsConfirming(false);
            destroy();
          });
          destroy();
          onConfirm(assignmentsByChannel);
        } catch {
          if (isDestroyedRef.current) {
            onValidationAbort?.();
            return;
          }

          setIsConfirming(false);
        }

        return;
      }

      destroy();
      onConfirm(assignmentsByChannel);
    } catch {
      setIsConfirming(false);
    }
  }, [
    shopifyPrepareAssets,
    assignmentsByChannel,
    destroy,
    onCancel,
    onConfirm,
    selectionAggregate.hasHardSelected,
    validateBeforeFinish,
    onValidationAbort,
  ]);

  const getBadgeCount = useCallback(
    (channelId) => countAssignedVariantsForChannel({
      variantMediaByChannel: assignmentsByChannel,
      channelId,
      totalVariantCount,
    }),
    [assignmentsByChannel, totalVariantCount],
  );

  const counterText = mediaStrings.COUNTER(assignedCount, totalVariantCount);

  const channelLabel = activeChannel?.label || '';

  const title = isMultiChannel
    ? mediaStrings.TITLE_MULTI
    : mediaStrings.TITLE_SINGLE(channelLabel);

  const description = isMultiChannel
    ? mediaStrings.DESCRIPTION_MULTI
    : mediaStrings.DESCRIPTION_SINGLE;

  return (
    <div className={`simpleDialog ${styles.connectChannelSectionModal} ${styles.connectChannelSectionModalWide} ${styles.connectChannelSectionDialogWithSubheader}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.connectChannelSectionModalBoxWide}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{title}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <DialogIcon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{description}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={isMultiChannel}>
              <ChannelTabsHeader
                channels={channels}
                activeChannelId={activeChannel.value}
                getBadgeCount={getBadgeCount}
                onChange={handleChannelChange}
              />
            </If>
            <div className={styles.connectChannelSectionVariantToolbar}>
              <Tooltip content={mediaStrings.COUNTER_TOOLTIP} placement="top">
                <span className={styles.connectChannelSectionVariantCounterInfo}>
                  <Icon size="lg">
                    <Info />
                  </Icon>
                </span>
              </Tooltip>
              <span className={styles.connectChannelSectionVariantCounter}>{counterText}</span>
              <button
                type="button"
                className={styles.connectChannelSectionVariantReset}
                onClick={handleResetAll}
              >
                {mediaStrings.RESET_ALL}
              </button>
            </div>
            <div className={styles.connectChannelSectionVariantList}>
              {variantOptions.map(({ id, label, attachedAssets }) => {
                const normalizedVariantId = normalizeVariantId(id);
                const selectedAssetIds = assetsByVariant[normalizedVariantId] || [];

                return (
                  <VariantMediaSection
                    key={id}
                    variantLabel={label}
                    poolAssets={attachedAssets}
                    selectedAssetIds={selectedAssetIds}
                    isExpanded={expandedVariantIds.has(normalizedVariantId)}
                    onToggleExpand={() => handleToggleExpand(id)}
                    onSelectAsset={(assetId) => handleSelectAsset(id, assetId)}
                    showValidationStatus={isActiveShopifyChannel}
                    requirePublishReady={requirePublishReadyForActiveChannel}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button
            color="secondary"
            size="md"
            variant="contained"
            disabled={isConfirming}
            onClick={handleCancel}
          >
            {strings.BTN_CANCEL}
          </Button>
          <If condition={onBack}>
            <Button
              color="secondary"
              size="md"
              variant="contained"
              disabled={isConfirming}
              onClick={handlePrevious}
            >
              {strings.BTN_PREVIOUS}
            </Button>
          </If>
          <Tooltip content={confirmDisabledTooltip}>
            <Button
              color="primary"
              size="md"
              variant="contained"
              disabled={isConfirmDisabled}
              endIcon={isConfirming ? <ButtonLoader /> : null}
              onClick={handleConfirm}
            >
              {strings.BTN_CONFIRM_VARIANT_MEDIA}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

VariantMediaAssignmentDialog.propTypes = {
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    type: PropTypes.string,
  })).isRequired,
  selectedChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
  channelActionsByChannelId: PropTypes.objectOf(PropTypes.string),
  variantOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    attachedAssets: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
    })).isRequired,
  })).isRequired,
  variantMediaByChannel: PropTypes.objectOf(PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))),
  validateBeforeFinish: PropTypes.func,
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  onValidationAbort: PropTypes.func,
};

VariantMediaAssignmentDialog.defaultProps = {
  channelActionsByChannelId: {},
  variantMediaByChannel: {},
  validateBeforeFinish: undefined,
  onBack: undefined,
  onCancel: undefined,
  onValidationAbort: undefined,
};

export function showVariantMediaAssignmentDialog(props) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <VariantMediaAssignmentDialog
      {...props}
      root={root}
    />,
  );
}

export default VariantMediaAssignmentDialog;
